/**
 * Pure derivations for the Cart page. No money is computed here — every amount comes
 * from the server-authoritative ledger on each group (see server/api-util/cartShipping.js).
 */

export const ROW_STATE_AVAILABLE = 'available';
export const ROW_STATE_PRICE_CHANGED = 'price_changed';
export const ROW_STATE_STOCK_REDUCED = 'stock_reduced';
export const ROW_STATE_SOLD = 'sold';
export const ROW_STATE_UNLISTED = 'unlisted';

export const SHIPPING_ROLE_BOX_RATE = 'box_rate';
export const SHIPPING_ROLE_RIDER = 'rider';
export const SHIPPING_ROLE_SOLO = 'solo';

const DEAD_ROW_STATES = [ROW_STATE_SOLD, ROW_STATE_UNLISTED];

/**
 * A dead row is visibly present but never charged. `sold` rows stay in their box and are
 * merely excluded from its ledger; `unlisted` rows have no seller and so belong to no box.
 *
 * @param {Object|null|undefined} row
 * @returns {boolean}
 */
export const isDeadRow = row => DEAD_ROW_STATES.includes(row?.state);

/**
 * Emptiness is a question about rows, never boxes.
 *
 * Unlisted rows deliberately belong to no box, so a cart whose every row went unlisted
 * has rows and zero groups. Branching on boxes would show "Your cart is empty" over a
 * cart that still has rows in it, destroying the only explanation the buyer gets.
 *
 * @param {Array<Object>|null|undefined} rows
 * @returns {boolean}
 */
export const isCartEmpty = rows => !Array.isArray(rows) || rows.length === 0;

/**
 * The old-vs-new price diff renders whenever the live price differs from the price at add,
 * *independently of the row's state*. A row that is both clamped and repriced reports only
 * `stock_reduced`, because the clamp is what moves the ledger — so a state-driven diff
 * would clamp and silently reprice the buyer in the same breath.
 *
 * @param {Object|null|undefined} row
 * @returns {boolean}
 */
export const hasPriceDiff = row =>
  row?.unitPriceAtAdd != null && row?.unitPrice != null && row.unitPrice !== row.unitPriceAtAdd;

/**
 * The listing stopped offering the method the buyer picked, so the row resolved to the
 * one still on offer. That relocates the row into another box and, for pickup, drops it
 * to $0 shipping, so it always renders.
 *
 * @param {Object|null|undefined} row
 * @returns {boolean}
 */
export const hasDeliveryMethodFallback = row =>
  !!row &&
  !isDeadRow(row) &&
  !!row.requestedDeliveryMethod &&
  !!row.deliveryMethod &&
  row.requestedDeliveryMethod !== row.deliveryMethod;

/**
 * A row can only offer the in-cart Shipping/Pickup toggle when its listing allows both.
 *
 * @param {Object|null|undefined} row
 * @returns {boolean}
 */
export const canSwitchDeliveryMethod = row =>
  !!row && !isDeadRow(row) && row.shippingEnabled === true && row.pickupEnabled === true;

/**
 * Extra units (other listings, plus the box row's 2nd+ qty) that ride in this box.
 *
 * @param {Object|null|undefined} ledger
 * @returns {number}
 */
export const riderUnitCount = ledger =>
  (ledger?.riders || []).reduce((sum, r) => sum + (r.unitCount || 0), 0);

/**
 * Extra shipping this listing adds as a rider. Zero when it rides for free.
 *
 * @param {Object|null|undefined} row
 * @param {Object|null|undefined} ledger
 * @returns {number}
 */
export const riderSurchargeForRow = (row, ledger) => {
  const rider = (ledger?.riders || []).find(r => r.listingId === row?.listingId);
  return typeof rider?.amount === 'number' ? rider.amount : 0;
};

/**
 * The stepper is only useful when stock leaves a second unit to buy.
 *
 * @param {Object|null|undefined} row
 * @returns {boolean}
 */
export const canAdjustQuantity = row =>
  !!row && !isDeadRow(row) && typeof row.stock === 'number' && row.stock > 1;

/**
 * Line total in subunits: live unit price × quantity.
 *
 * @param {Object|null|undefined} row
 * @returns {number|null}
 */
export const lineItemTotalSubunits = row => {
  if (typeof row?.unitPrice !== 'number' || typeof row?.qty !== 'number') {
    return null;
  }
  return row.unitPrice * row.qty;
};

/**
 * Sum of live line totals in a box. Dead rows are never charged.
 *
 * @param {Array<Object>|null|undefined} rows
 * @returns {number}
 */
export const liveItemsSubtotalSubunits = rows =>
  (Array.isArray(rows) ? rows : [])
    .filter(r => !isDeadRow(r))
    .reduce((sum, r) => sum + (lineItemTotalSubunits(r) || 0), 0);

/**
 * Items plus this box's shipping (pickup shipping is 0).
 *
 * @param {Array<Object>|null|undefined} rows
 * @param {Object|null|undefined} ledger
 * @returns {number}
 */
export const boxGrandTotalSubunits = (rows, ledger) =>
  liveItemsSubtotalSubunits(rows) + (ledger?.total || 0);

/**
 * How this live shipping row participates in the box fee. Pickup and dead rows have
 * no role — they do not set or ride a shipping rate.
 *
 * @param {Object|null|undefined} row
 * @param {Object|null|undefined} ledger
 * @returns {'box_rate'|'rider'|'solo'|null}
 */
export const shippingRoleForRow = (row, ledger) => {
  if (!row || isDeadRow(row) || row.deliveryMethod === 'pickup') {
    return null;
  }
  if (riderUnitCount(ledger) === 0) {
    return SHIPPING_ROLE_SOLO;
  }
  return row.listingId === ledger?.maxFeeRowId ? SHIPPING_ROLE_BOX_RATE : SHIPPING_ROLE_RIDER;
};

/**
 * Number the boxes of any seller who owns more than one. Sellers with a single box get
 * no label, because "BOX 1" of one box explains nothing.
 *
 * @param {Array<Object>} groups
 * @returns {Object} group key -> 1-based box number, or absent when unlabelled
 */
export const boxNumbersByGroupKey = groups => {
  const all = Array.isArray(groups) ? groups : [];
  const totals = all.reduce((acc, group) => {
    const sellerId = group?.sellerId;
    return sellerId ? { ...acc, [sellerId]: (acc[sellerId] || 0) + 1 } : acc;
  }, {});

  const running = {};
  return all.reduce((acc, group) => {
    const sellerId = group?.sellerId;
    if (!sellerId || totals[sellerId] < 2) {
      return acc;
    }
    running[sellerId] = (running[sellerId] || 0) + 1;
    return { ...acc, [group.key]: running[sellerId] };
  }, {});
};

/**
 * Split the revalidate response into boxes and the "No longer available" section.
 *
 * Any row the server could not attribute to a box lands in the unavailable section —
 * that is the whole reason unlisted rows come back with `sellerId: null`, and it is also
 * what catches a listing that stopped offering any delivery method.
 *
 * @param {Object} params
 * @param {Array<Object>} params.rows
 * @param {Array<Object>} params.groups
 * @returns {{ boxes: Array<Object>, unavailableRows: Array<Object> }}
 */
export const partitionCartRows = ({ rows, groups }) => {
  const allRows = Array.isArray(rows) ? rows : [];
  const allGroups = Array.isArray(groups) ? groups : [];

  const rowsById = new Map(allRows.filter(r => r?.listingId).map(r => [r.listingId, r]));
  const boxNumbers = boxNumbersByGroupKey(allGroups);
  const sellerTotals = allGroups.reduce((acc, group) => {
    const sellerId = group?.sellerId;
    return sellerId ? { ...acc, [sellerId]: (acc[sellerId] || 0) + 1 } : acc;
  }, {});
  const grouped = new Set();

  const boxes = allGroups.map(group => {
    const groupRows = (group.listingIds || [])
      .map(id => {
        grouped.add(id);
        return rowsById.get(id);
      })
      .filter(Boolean);

    return {
      ...group,
      rows: groupRows,
      liveRows: groupRows.filter(r => !isDeadRow(r)),
      boxNumber: boxNumbers[group.key] || null,
      sellerBoxCount: group.sellerId ? sellerTotals[group.sellerId] || 1 : 1,
    };
  });

  return {
    boxes,
    unavailableRows: allRows.filter(r => r?.listingId && !grouped.has(r.listingId)),
  };
};

/**
 * The merge notice reports only what actually changed, so an all-zero summary renders
 * nothing at all. No "welcome back, your cart is unchanged".
 *
 * @param {Object|null|undefined} summary
 * @returns {boolean}
 */
export const mergeSummaryHasContent = summary =>
  !!summary &&
  ((summary.carriedOver || 0) > 0 ||
    (summary.conflicts?.length || 0) > 0 ||
    (summary.droppedForCap || 0) > 0);
